import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  Avatar,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { companyService, CompanySettings as CompanySettingsType } from '../../services/companyService';

const CompanySettings: React.FC = () => {
  const navigate = useNavigate();
  
  const [companySettings, setCompanySettings] = useState<CompanySettingsType | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [logoTimestamp, setLogoTimestamp] = useState<number>(Date.now());
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  const fetchCompanySettings = async (skipLoadingState = false) => {
    try {
      if (!skipLoadingState) setLoading(true);
      setError(null);
      const settings = await companyService.getCompanySettings();
      console.log('Fetched company settings:', settings);
      setCompanySettings(settings);
      setCompanyName(settings.companyName);
      setLogoTimestamp(Date.now()); // Set initial timestamp when loading
      return settings; // Return the fetched settings
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load company settings');
      return null;
    } finally {
      if (!skipLoadingState) setLoading(false);
    }
  };

  const handleCompanyNameSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (!companyName.trim()) {
        setError('Company name cannot be empty');
        return;
      }

      const updatedSettings = await companyService.updateCompanyName(companyName.trim());
      setCompanySettings(updatedSettings);
      setSuccess('Company name updated successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update company name');
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (PNG, JPG, etc.)');
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setSelectedFile(file);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setError(null);
    }
  };

  const handleLogoUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const updatedSettings = await companyService.uploadLogo(selectedFile);
      
      // Small delay to ensure backend has processed the upload
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Force refresh by fetching latest data from server
      const refreshedSettings = await fetchCompanySettings(true); // Skip loading state
      
      setLogoTimestamp(Date.now()); // Update timestamp only when logo changes
      setRefreshTrigger(prev => prev + 1); // Trigger component refresh
      setSelectedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      
      // Use refreshed data for success message
      const newFilename = refreshedSettings?.logoFilename || 'unknown';
      console.log('Setting success message with filename:', newFilename);
      setSuccess(`Logo uploaded successfully! New filename: ${newFilename}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      setDeleting(true);
      setError(null);
      setSuccess(null);

      const updatedSettings = await companyService.deleteLogo();
      
      // Force refresh by fetching latest data from server
      await fetchCompanySettings(true); // Skip loading state
      
      setLogoTimestamp(Date.now()); // Update timestamp when logo is deleted
      setDeleteDialogOpen(false);
      setSuccess('Logo deleted successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete logo');
    } finally {
      setDeleting(false);
    }
  };

  const cancelFileSelection = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setError(null);
  };

  const getCurrentLogoUrl = () => {
    if (companySettings?.logoUrl && companySettings?.logoFilename) {
      // Add timestamp to prevent caching issues
      return companyService.getLogoUrl(companySettings.logoFilename) + '?t=' + Date.now();
    }
    return null;
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Company Settings
          </Typography>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Company Name Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Company Name
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Enter your company name"
                    disabled={saving}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    onClick={handleCompanyNameSave}
                    disabled={saving || !companyName.trim() || companyName.trim() === companySettings?.companyName}
                    fullWidth
                  >
                    {saving ? 'Saving...' : 'Save Name'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Company Logo Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Company Logo
              </Typography>
              
              <Grid container spacing={3}>
                {/* Current Logo Display */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Current Logo
                      </Typography>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar
                          key={`current-logo-${companySettings?.logoFilename || 'none'}-${logoTimestamp}-${refreshTrigger}`}
                          src={getCurrentLogoUrl() || undefined}
                          sx={{ 
                            width: 80, 
                            height: 80,
                            bgcolor: 'primary.main'
                          }}
                        >
                          <BusinessIcon sx={{ fontSize: 40 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {companySettings?.logoFilename || 'No logo uploaded'}
                          </Typography>
                          {companySettings?.logoFilename && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => setDeleteDialogOpen(true)}
                              sx={{ mt: 1 }}
                            >
                              Delete Logo
                            </Button>
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Logo Upload */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Upload New Logo
                      </Typography>
                      
                      {selectedFile ? (
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                              src={previewUrl || undefined}
                              sx={{ 
                                width: 60, 
                                height: 60,
                                bgcolor: 'primary.main'
                              }}
                            >
                              <BusinessIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="body2">
                                {selectedFile.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button
                              variant="contained"
                              startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                              onClick={handleLogoUpload}
                              disabled={uploading}
                              size="small"
                            >
                              {uploading ? 'Uploading...' : 'Upload'}
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={cancelFileSelection}
                              disabled={uploading}
                              size="small"
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Stack>
                      ) : (
                        <Box>
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="logo-upload"
                            type="file"
                            onChange={handleFileSelect}
                          />
                          <label htmlFor="logo-upload">
                            <Button
                              variant="outlined"
                              component="span"
                              startIcon={<PhotoCameraIcon />}
                              fullWidth
                            >
                              Select Logo Image
                            </Button>
                          </label>
                          <FormHelperText>
                            Supported formats: PNG, JPG, GIF. Max size: 5MB
                          </FormHelperText>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Preview Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This is how your company branding will appear in the application:
              </Typography>
              
              <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                                   <Avatar
                   key={`preview-${companySettings?.logoFilename || 'none'}-${logoTimestamp}-${refreshTrigger}`}
                   src={previewUrl || getCurrentLogoUrl() || undefined}
                   sx={{ 
                     width: 64, 
                     height: 64,
                     bgcolor: 'primary.main'
                   }}
                 >
                   <BusinessIcon sx={{ fontSize: 32 }} />
                 </Avatar>
                  <Typography variant="h5" color="primary" fontWeight="bold">
                    {companyName || companySettings?.companyName || 'Your Company Name'}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Logo Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Delete Company Logo
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete the current company logo? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleLogoDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              {deleting ? 'Deleting...' : 'Delete Logo'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CompanySettings; 